/* eslint-disable no-restricted-globals */
import React, { useEffect, useMemo, useRef, useState } from "react";

import ShipeeziNewGrid, { Category, Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { Color } from "../../../../../../../components/Form/Button";
import { Brand } from "../../../../../../../models/brand";
import { Filters, Searches } from "../../../../../../../models/shipeezi.table";
import { Status } from "../../../../../../../util/enums";
import BrandModal, { BrandModalHandle } from "./components/BrandModal";
import ChangeStatusButton from "./components/ChangeStatusButton";
import DeleteBrandModal, {
  DeleteBrandConfirmationModalHandles,
} from "./components/DeleteBrandModal";
import DeleteBrandButton from "./components/DeleteButton";
import { BrandEmptySection } from "./components/EmptySection";
import BadgeStatus from "./components/StatusBadge";
import useAction from "./hooks/useAction";
import useStyles, { ChangeStatusIcon, DeleteIcon, NoWrapButton, PlusIcon } from "./styles";
import { useAppState } from "Context/StateContext";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const Brands = ({ permission }: Props) => {
  const { t: translate } = useTranslation();
  const {
    getAllBrands,
    useDeleteBrands,
    useCreateBrand,
    useUpdateBrand,
    useUpdateBrandList,
    useDeleteBrandsList,
  } = useAction();
  const classes = useStyles();
  const deleteModalRef = useRef<DeleteBrandConfirmationModalHandles>(null);
  const {currentUser} = useAppState();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [totalItems, setTotalItems] = useState<number>(100);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>({});
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<number>();
  const brandModalRef = useRef<BrandModalHandle>(null);
  const { data, isLoading, isRefetching, refetch } = getAllBrands(
    page,
    pageSize,
    searches,
    filters
  );

  const afterSave = () => {
    brandModalRef?.current?.close();
    refetch();
  };

  const afterDelete = () => {
    deleteModalRef?.current?.close();
    setSelectedItems([]);
    refetch();
  };

  const selectedBrands = useMemo(
    () => selectedItems.map((value) => brands.find((brand) => brand.id === value)),
    [selectedItems]
  );

  const { mutate: createBrand, isLoading: isLoadingCreate } = useCreateBrand(afterSave);
  const { mutate: updateBrand, isLoading: isLoadingUpdate } = useUpdateBrand(afterSave);
  const { mutate: deleteBrand, isLoading: loadingDelete } = useDeleteBrands(
    selectedBrandId || 0,
    afterDelete
  );
  const { mutate: updateList, isLoading: isLoadingUpdateList } = useUpdateBrandList(() =>
    refetch()
  );
  const { mutate: deleteList, isLoading: isLoadingDeleteList } = useDeleteBrandsList(
    selectedItems,
    afterDelete
  );

  useEffect(() => {
    if (data && data.rows) {
      setBrands(data.rows);
      setTotalItems(data.count);
    }
  }, [data]);

  const columns: Column[] = [
    {
      name: "name",
      title: `${translate("brandName")}`,
    },
    {
      name: "code",
      title: `${translate("brandCode")}`,
    },
    {
      name: "status",
      title: `${translate("status")}`,
      formatComponent: (value?: string) => <BadgeStatus value={value} />,
      hideSearch: true,
    },
  ];
  const renderAddBrandButton = () => {
    return (
      <NoWrapButton
        onClick={() => onOpenBrandModal()}
        customColor={Color.PRIMARY}
        data-cy={"addBrandbutton"}
      >
        <PlusIcon color="#ffffff" size={16} />
        {translate("addNewBrandName")}
      </NoWrapButton>
    );
  };
  const renderDeleteButton = () => {
    return (
      <NoWrapButton
        onClick={() => onOpenDeleteModal(selectedItems)}
        customColor={Color.CANCEL}
        className={classes.deleteBorder}
      >
        <DeleteIcon color="#F04438" size={16} />
        {translate("deleteBrands")}
      </NoWrapButton>
    );
  };
  const onChangeStatus = () => {
    if (selectedBrands && selectedBrands?.length) {
      const brandsToUpdate: Brand[] = selectedBrands?.map((value) => ({
        ...value,
        status: value?.status === Status.ACTIVE ? Status?.INACTIVE : Status.ACTIVE,
      }));

      updateList(brandsToUpdate);
    }
  };

  const renderChangeStatusButton = () => {
    if (selectedBrands?.length) {
      const firstStatus = selectedBrands?.[0]?.status;
      const valid = selectedBrands?.every((value) => value?.status === firstStatus);

      if (valid) {
        return (
          <NoWrapButton
            onClick={() => onChangeStatus()}
            customColor={Color.SECONDARY_OUTLINED}
            loading={isLoadingUpdateList}
          >
            <ChangeStatusIcon color="#0055A4" size={18} />
            {translate("changeStatus")}
          </NoWrapButton>
        );
      }
    }
  };

  const handleChangeCurrentPage = (currentPage: any) => {
    setPage(currentPage - 1);
  };

  const handleChangeRowsPerPage = async (newPageSize: any) => {
    setPageSize(newPageSize);
  };

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const filterCategories: Category[] = [
    {
      name: translate("status"),
      column: "status",
      options: [
        { value: Status.ACTIVE, label: translate("active") },
        { value: Status.INACTIVE, label: translate("inactive") },
      ],
    },
  ];

  const onOpenDeleteModal = (ids: number[]) => {
    let brandName: string = "";

    if (ids?.length === 1) {
      setSelectedBrandId(ids?.[0]);
      brandName = brands?.find((value) => value?.id === ids?.[0])?.name || "";
    } else {
      setSelectedBrandId(undefined);
    }

    deleteModalRef?.current?.open(ids, brandName);
  };
  const onDeleteBrand = (ids: number[]) => {
    if (ids?.length) {
      if (ids?.length === 1) {
        deleteBrand(ids[0]);
      } else {
        const brandsToDelete: Brand[] = [];

        for (const item of selectedItems) {
          const find = brands?.find((value) => value?.id === item);

          if (find) {
            brandsToDelete.push(find);
          }
        }

        if (brandsToDelete?.length) {
          deleteList(brandsToDelete);
        }
      }
    }
  };

  const onSaveBrand = (brand: Brand) => {
    if (brand) {
      if (brand?.id) {
        updateBrand({ brand, id: brand?.id });
      } else {
        createBrand({ brand });
      }
    }
  };
  const renderDeleteAction = (id: number) => {
    return (
      <DeleteBrandButton
        ids={[id]}
        handleOnClick={onOpenDeleteModal}
        loading={loadingDelete && id === selectedBrandId}
      />
    );
  };
  const renderChangeStatusAction = (id: number) => {
    const onChangeStatus = (brandId: number) => {
      setSelectedBrandId(brandId);
      const brand = brands.find((value) => value?.id === brandId);

      if (brand) {
        return onSaveBrand({
          ...brand,
          status: brand?.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        });
      }
    };

    return (
      <ChangeStatusButton
        id={id}
        handleOnClick={onChangeStatus}
        loading={isLoadingUpdate && id === selectedBrandId}
      />
    );
  };

  const onOpenBrandModal = (brand?: Brand, view?: boolean) => {
    brandModalRef?.current?.open(brand, view);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: selectedItems?.length > 1 ? ["onChangeStatus", "onDelete"] : ["onAddBrand"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: filterCategories,
      onChange: handleFilter,
    },
    onAddBrand: permission && isShowAction(currentUser, permission.create)
      ? renderAddBrandButton()
      : undefined,
    onChangeStatus: renderChangeStatusButton(),
    onDelete: renderDeleteButton(),
  };

  const renderScreen = () => {
    if (
      !isLoading &&
      !searches.length &&
      !Object.keys(filters).length &&
      (!data || !data?.rows?.length)
    ) {
      return (
        <div>
          <BrandEmptySection onAdd={() => onOpenBrandModal()} />
        </div>
      );
    }

    return (
      <div data-cy="brandListGrid">
        <ShipeeziNewGrid
          data={brands}
          totalItems={totalItems}
          loadingData={isLoading || isRefetching}
          namePage={translate("brandName")}
          emptyData={isLoading ? translate("loadingBrands") : translate("noInformationToBeShown")}
          columns={columns}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={totalItems > pageSize ? [20, 40, 60, 100] : []}
          onChangePageSize={handleChangeRowsPerPage}
          onNextPage={handleChangeCurrentPage}
          onPreviousPage={handleChangeCurrentPage}
          onGoToPage={handleChangeCurrentPage}
          generalActions={generalActions}
          rowActions={{
            handleDetails: (brandId, brand: Brand) => onOpenBrandModal(brand, true),
            handleEdit: permission && isShowAction(currentUser, permission.edit)
              ? (brandId, brand: Brand) => onOpenBrandModal(brand)
              : undefined,
          }}
          rowActionsExtra={[
            {
              title: translate("changeStatus"),
              key: "changeStatus",
              buttonAndAction: renderChangeStatusAction,
            },
            {
              title: translate("delete"),
              key: "delete",
              buttonAndAction: renderDeleteAction,
            },
          ]}
          positionActionsColumn="right"
          selecteds={selectedItems}
          handleSelect={setSelectedItems}
          showFooterGrid={totalItems !== 0}
          showColumnChooser={false}
        />
      </div>
    );
  };

  return (
    <>
      {renderScreen()}
      <BrandModal
        onSubmit={onSaveBrand}
        onDelete={onOpenDeleteModal}
        isLoading={isLoadingCreate || isLoadingUpdate || loadingDelete}
        ref={brandModalRef}
      />
      <DeleteBrandModal
        ref={deleteModalRef}
        onSubmit={onDeleteBrand}
        loadingAction={loadingDelete || isLoadingDeleteList}
      />
    </>
  );
};

export default Brands;
